import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {useForm, Controller} from "react-hook-form";
import {
	App as AntdApp, Card, Col, Row, Progress, Tag, Table, Tabs, Descriptions,
	Button as AntdButton, Space, Popconfirm, Timeline, Alert, Statistic, Select,
} from "antd";
import dayjs from "dayjs";
import {Loading, FontAwesomeIcon, Button} from "~/components";
import {InputField, NumberField, SelectField, TextAreaField, DateField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetMemberQuery, useSetMemberPlanMutation, useSetMemberTokensMutation, useGetTokenLedgerQuery,
	useSetMemberStatusMutation, useMemberLogoutAllMutation, useResetMemberPasswordMutation, useGetMemberActivityQuery,
	useGetMemberAiKeyQuery, useSetMemberAiKeyMutation, useDeleteMemberAiKeyMutation,
} from "~/reduxs/api/memberApiSlice";
import {useGetAdminPlansQuery} from "~/reduxs/api/planApiSlice";
import {nf, statusMeta, STATUS_OPTIONS} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const barColor = (pct) => (pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a');

/** Chữ cái đầu (tối đa 2) cho avatar. */
const initials = (name = '') => {
	const parts = String(name).trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function LimitBar({icon, label, used, limit, resetAt, unlimited}) {
	const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));

	return (
		<Card className={cn('limit-card')} size="small">
			<div className={cn('limit-head')}>
				<span className={cn('limit-label')}><FontAwesomeIcon icon={icon} /> {label}</span>
				<span className={cn('limit-value')}>{unlimited ? 'Không giới hạn' : `${nf(used)} / ${nf(limit)}`}</span>
			</div>
			<Progress percent={unlimited ? 100 : pct} showInfo={false} strokeColor={unlimited ? '#7c3aed' : barColor(pct)} />
			{!unlimited && resetAt && (
				<div className={cn('limit-reset')}>Làm mới lúc {dayjs(resetAt).format('HH:mm DD/MM/YYYY')}</div>
			)}
		</Card>
	);
}

function MemberDetail() {
	const {id} = useParams();
	const {notification} = AntdApp.useApp();

	const {data: m, isLoading, isError} = useGetMemberQuery(id);
	// Danh sách gói của ADMIN (gồm cả gói ẩn status=inactive) để có thể gán gói đặc biệt.
	const {data: plans = []} = useGetAdminPlansQuery();

	if (isLoading) return <Loading />;

	if (isError || !m) {
		return (
			<div className="container">
				<Alert type="error" showIcon message="Không tải được thông tin thành viên." />
			</div>
		);
	}

	const sm = statusMeta(m.status);
	const u = m.usage || {};
	const isRoot = Boolean(u.is_root);

	const stats = [
		{icon: 'fa-light fa-folder-tree', label: 'Dự án', value: isRoot ? '∞' : `${nf(u.projects?.used)} / ${nf(u.projects?.limit)}`},
		{icon: 'fa-light fa-wand-magic-sparkles', label: 'Token còn lại (tháng)', value: isRoot ? 'Không giới hạn' : nf(u.ai_tokens?.remaining)},
		{icon: 'fa-light fa-link', label: 'URL còn lại (tuần)', value: isRoot ? 'Không giới hạn' : nf(u.url_checks?.remaining)},
		{icon: 'fa-light fa-clock-rotate-left', label: 'Đăng nhập gần nhất', value: m.last_login ? dayjs(m.last_login).format('HH:mm DD/MM') : 'Chưa có'},
	];

	const tab = (icon, text) => <span><FontAwesomeIcon icon={icon} /> {text}</span>;

	return (
		<div className="container">
			<div className={cn('topbar')}>
				<Button to="/admin/members" white leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-left" />}>
					Danh sách thành viên
				</Button>
			</div>

			<div className={cn('profile')}>
				<div className={cn('avatar')}>{initials(m.fullname)}</div>

				<div className={cn('profile-main')}>
					<div className={cn('profile-name-row')}>
						<h2 className={cn('profile-name')}>{m.fullname}</h2>
						<Tag color={sm.color}>{sm.label}</Tag>
						{isRoot && <Tag color="gold">Quản trị</Tag>}
					</div>
					<div className={cn('profile-meta')}>
						<span><FontAwesomeIcon icon="fa-light fa-at" /> {m.username}</span>
						{m.email && <span><FontAwesomeIcon icon="fa-light fa-envelope" /> {m.email}</span>}
						{m.phone && <span><FontAwesomeIcon icon="fa-light fa-phone" /> {m.phone}</span>}
						{m.role && <span><FontAwesomeIcon icon="fa-light fa-user-tag" /> {m.role}</span>}
					</div>
				</div>

				<div className={cn('profile-plan')} data-plan={m.plan?.slug}>
					<span className={cn('plan-caption')}>Gói hiện tại</span>
					<span className={cn('plan-badge')}><FontAwesomeIcon icon="fa-light fa-crown" /> {m.plan?.name}</span>
				</div>
			</div>

			<div className={cn('stats')}>
				{stats.map((s, i) => (
					<div className={cn('stat')} key={i}>
						<span className={cn('stat-icon')}><FontAwesomeIcon icon={s.icon} /></span>
						<div className={cn('stat-body')}>
							<div className={cn('stat-value')}>{s.value}</div>
							<div className={cn('stat-label')}>{s.label}</div>
						</div>
					</div>
				))}
			</div>

			<Card className={cn('tabs-card')} variant="borderless">
				<Tabs
					defaultActiveKey="overview"
					items={[
						{key: 'overview', label: tab('fa-light fa-chart-simple', 'Tổng quan'), children: <OverviewTab m={m} />},
						{key: 'plan', label: tab('fa-light fa-layer-group', 'Gói'), children: <PlanTab id={id} m={m} plans={plans} notification={notification} />},
						{key: 'tokens', label: tab('fa-light fa-wand-magic-sparkles', 'AI Token'), children: <TokenTab id={id} m={m} notification={notification} />},
						{key: 'aikey', label: tab('fa-light fa-key', 'Khoá AI'), children: <ApiKeyTab id={id} notification={notification} />},
						{key: 'security', label: tab('fa-light fa-shield-halved', 'Trạng thái & Bảo mật'), children: <SecurityTab id={id} m={m} notification={notification} />},
						{key: 'activity', label: tab('fa-light fa-timeline', 'Hoạt động'), children: <ActivityTab id={id} />},
					]}
				/>
			</Card>
		</div>
	);
}

/* ===================== Tab: Tổng quan ===================== */
function OverviewTab({m}) {
	const u = m.usage || {};
	const isRoot = Boolean(u.is_root);

	return (
		<>
			<div className={cn('section-title')}>Hạn mức sử dụng</div>
			<Row gutter={[16, 16]} className={cn('limits-grid')}>
				<Col xs={24} md={8}>
					<LimitBar icon="fa-light fa-wand-magic-sparkles" label="AI Token (tháng)"
						used={u.ai_tokens?.used} limit={u.ai_tokens?.limit} resetAt={u.ai_tokens?.reset_at} unlimited={isRoot} />
				</Col>
				<Col xs={24} md={8}>
					<LimitBar icon="fa-light fa-link" label="URL kiểm tra (tuần)"
						used={u.url_checks?.used} limit={u.url_checks?.limit} resetAt={u.url_checks?.reset_at} unlimited={isRoot} />
				</Col>
				<Col xs={24} md={8}>
					<LimitBar icon="fa-light fa-folder-tree" label="Dự án"
						used={u.projects?.used} limit={u.projects?.limit} unlimited={isRoot} />
				</Col>
			</Row>

			<div className={cn('section-title')} style={{marginTop: 24}}>Thông tin tài khoản</div>
			<Descriptions bordered column={{xs: 1, sm: 2}} size="small">
				<Descriptions.Item label="Email">{m.email || '—'}</Descriptions.Item>
				<Descriptions.Item label="Số điện thoại">{m.phone || '—'}</Descriptions.Item>
				<Descriptions.Item label="Chức vụ">{m.role || '—'}</Descriptions.Item>
				<Descriptions.Item label="Ngày tạo">{m.created ? dayjs(m.created).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
				<Descriptions.Item label="Đăng nhập gần nhất">
					{m.last_login ? dayjs(m.last_login).format('HH:mm DD/MM/YYYY') : '—'}
					{m.last_login_ip ? ` (${m.last_login_ip})` : ''}
				</Descriptions.Item>
				<Descriptions.Item label="Số dự án">{nf(m.counts?.projects)}</Descriptions.Item>
				<Descriptions.Item label="Báo cáo SEO">{nf(m.counts?.seo_reports)}</Descriptions.Item>
				<Descriptions.Item label="Báo cáo PageSpeed">{nf(m.counts?.psi_reports)}</Descriptions.Item>
				<Descriptions.Item label="Lượt dùng AI">{nf(m.counts?.ai_history)}</Descriptions.Item>
			</Descriptions>
		</>
	);
}

/* ===================== Tab: Gói ===================== */
function PlanTab({id, m, plans, notification}) {
	const [setPlan, {isLoading}] = useSetMemberPlanMutation();
	const sub = m.subscription || {};

	const {control, handleSubmit} = useForm({
		defaultValues: {plan_slug: sub.plan_slug || 'free', expires: sub.expires || null, note: ''},
	});

	const submit = async (values) => {
		try {
			await setPlan({
				id,
				plan_slug: values.plan_slug,
				expires: values.expires ? dayjs(values.expires).format('YYYY-MM-DD HH:mm:ss') : '',
				note: values.note || '',
			}).unwrap();
			notification.success({message: 'Đã cập nhật gói'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const cancel = async () => {
		try {
			await setPlan({id, action: 'cancel'}).unwrap();
			notification.success({message: 'Đã hủy gói (về mặc định miễn phí)'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	return (
		<Row gutter={[24, 16]}>
			<Col xs={24} md={14}>
				<div className={cn('section-title')}>Đổi gói cho thành viên</div>
				<div className="form">
					<Controller control={control} name="plan_slug" render={({field}) => (
						<SelectField label="Gói"
							options={plans.map((p) => ({value: p.slug, label: `${p.name}${p.price > 0 ? ` — ${nf(p.price)}đ` : ''}${p.status !== 'active' ? ' (ẩn)' : ''}`}))}
							{...field} />
					)} />
					<Controller control={control} name="expires" render={({field}) => (
						<DateField label="Ngày hết hạn (để trống = không hạn)" showTime {...field} />
					)} />
					<Controller control={control} name="note" render={({field}) => (
						<TextAreaField label="Ghi chú" rows={2} maxLength={500} placeholder="Lý do đổi gói (tùy chọn)" {...field} />
					)} />
					<Space>
						<AntdButton type="primary" onClick={handleSubmit(submit)} loading={isLoading}>Cập nhật gói</AntdButton>
						<Popconfirm title="Hủy gói của thành viên?" onConfirm={cancel} okText="Hủy gói" cancelText="Thôi">
							<AntdButton danger>Hủy gói</AntdButton>
						</Popconfirm>
					</Space>
				</div>
			</Col>
			<Col xs={24} md={10}>
				<div className={cn('section-title')}>Gói hiện tại</div>
				<Descriptions bordered column={1} size="small">
					<Descriptions.Item label="Gói">{sub.plan_slug || 'free'}</Descriptions.Item>
					<Descriptions.Item label="Trạng thái">{sub.status || 'active'}</Descriptions.Item>
					<Descriptions.Item label="Bắt đầu">{sub.started ? dayjs(sub.started).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
					<Descriptions.Item label="Hết hạn">{sub.expires ? dayjs(sub.expires).format('DD/MM/YYYY') : 'Không hạn'}</Descriptions.Item>
				</Descriptions>
			</Col>
		</Row>
	);
}

/* ===================== Tab: AI Token ===================== */
function TokenTab({id, m, notification}) {
	const [delta, setDelta] = useState(0);
	const [note, setNote] = useState('');
	const [reasonFilter, setReasonFilter] = useState('all');
	const [setTokens, {isLoading}] = useSetMemberTokensMutation();
	const {data: ledger = []} = useGetTokenLedgerQuery(id);

	const filteredLedger = reasonFilter === 'all' ? ledger : ledger.filter((r) => r.reason === reasonFilter);

	const t = m.usage?.ai_tokens || {};
	const bonus = m.subscription?.token_bonus || 0;

	const apply = async () => {
		if (!delta) {
			notification.warning({message: 'Nhập số token cộng/trừ (khác 0)'});
			return;
		}
		try {
			await setTokens({id, delta, note}).unwrap();
			notification.success({message: 'Đã cập nhật token'});
			setDelta(0);
			setNote('');
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const reset = async () => {
		try {
			await setTokens({id, reset: true}).unwrap();
			notification.success({message: 'Đã reset token tháng này'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const REASON_LABELS = {
		consume: 'Tiêu dùng AI',
		admin_add: 'Admin cộng',
		admin_deduct: 'Admin trừ',
		reset: 'Reset tháng',
		bonus: 'Token thưởng',
	};

	const ledgerColumns = [
		{
			title: 'Thay đổi', dataIndex: 'delta', key: 'delta',
			render: (v) => <span style={{color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600}}>{v >= 0 ? '+' : ''}{nf(v)}</span>,
		},
		{title: 'Lý do', dataIndex: 'reason', key: 'reason', render: (v) => REASON_LABELS[v] || v},
		{title: 'Ghi chú', dataIndex: 'note', key: 'note', render: (v) => v || '—'},
		{title: 'Thời gian', dataIndex: 'created', key: 'created', render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—')},
	];

	return (
		<>
			<Row gutter={16} style={{marginBottom: 18}}>
				<Col xs={12} md={6}><Statistic title="Đã dùng (tháng)" value={t.used || 0} /></Col>
				<Col xs={12} md={6}><Statistic title="Hạn mức" value={t.limit || 0} /></Col>
				<Col xs={12} md={6}><Statistic title="Còn lại" value={t.remaining || 0} /></Col>
				<Col xs={12} md={6}><Statistic title="Token thưởng (bonus)" value={bonus} /></Col>
			</Row>

			<div className="form" style={{maxWidth: 520, marginBottom: 20}}>
				<Row gutter={12}>
					<Col span={12}>
						<NumberField label="Cộng (+) / Trừ (−) token" value={delta} onChange={(v) => setDelta(v || 0)} step={1000} placeholder="vd: 5000 hoặc -2000" />
					</Col>
					<Col span={12}>
						<InputField label="Ghi chú (tùy chọn)" value={note} onChange={(e) => setNote(e.target.value)} />
					</Col>
				</Row>
				<Space>
					<AntdButton type="primary" loading={isLoading} onClick={apply}>Áp dụng</AntdButton>
					<Popconfirm title="Reset bộ đếm token tháng này về 0?" onConfirm={reset} okText="Reset" cancelText="Thôi">
						<AntdButton>Reset tháng</AntdButton>
					</Popconfirm>
				</Space>
			</div>

			<div className={cn('section-title')} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
				<span>Lịch sử cộng/trừ token</span>
				<Select
					size="small"
					value={reasonFilter}
					onChange={setReasonFilter}
					style={{minWidth: 150}}
					options={[
						{value: 'all', label: 'Tất cả loại'},
						...Object.entries(REASON_LABELS).map(([value, label]) => ({value, label})),
					]}
				/>
			</div>
			<Table columns={ledgerColumns} dataSource={filteredLedger} rowKey="id" size="small" pagination={{pageSize: 10}} />
		</>
	);
}

/* ===================== Tab: Khoá AI riêng (BYO — admin cấu hình) ===================== */
const AI_PROVIDERS = [
	{value: 'gemini', label: 'Google Gemini (miễn phí)'},
	{value: 'openai', label: 'OpenAI-compatible (Groq / OpenRouter / OpenAI)'},
	{value: 'claude', label: 'Anthropic Claude'},
];

const AI_HINTS = {
	gemini: {model: 'gemini-2.5-flash', get: 'aistudio.google.com/apikey'},
	openai: {model: 'llama-3.3-70b-versatile', get: 'console.groq.com/keys'},
	claude: {model: 'claude-haiku-4-5', get: 'console.anthropic.com'},
};

function ApiKeyTab({id, notification}) {
	const {data, isLoading} = useGetMemberAiKeyQuery(id);
	const [saveKey, {isLoading: saving}] = useSetMemberAiKeyMutation();
	const [deleteKey, {isLoading: deleting}] = useDeleteMemberAiKeyMutation();

	const {control, handleSubmit, watch, reset, formState: {errors}} = useForm({
		defaultValues: {provider: 'gemini', api_key: '', model: '', base_url: ''},
	});

	const provider = watch('provider');
	const configured = Boolean(data?.configured);

	useEffect(() => {
		if (data?.configured) {
			reset({provider: data.provider || 'gemini', api_key: '', model: data.model || '', base_url: data.base_url || ''});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const submit = async (values) => {
		try {
			await saveKey({
				id,
				provider: values.provider,
				api_key: values.api_key.trim(),
				model: values.model?.trim() || '',
				base_url: values.base_url?.trim() || '',
			}).unwrap();
			notification.success({message: 'Đã lưu khoá AI cho thành viên'});
			reset({...values, api_key: ''});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const remove = async () => {
		try {
			await deleteKey(id).unwrap();
			notification.success({message: 'Đã xoá khoá riêng — thành viên dùng khoá hệ thống'});
			reset({provider: 'gemini', api_key: '', model: '', base_url: ''});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	if (isLoading) return <Loading />;

	const hint = AI_HINTS[provider] || AI_HINTS.gemini;

	return (
		<Row gutter={[24, 16]}>
			<Col xs={24} md={16}>
				<div className={cn('section-title')}>Khoá AI riêng của thành viên</div>
				{configured ? (
					<Alert type="success" showIcon style={{marginBottom: 16}}
						message={<span>Đang dùng khoá riêng — <Tag color="purple">{data.provider}</Tag> <code>{data.masked}</code>{data.model ? ` · ${data.model}` : ''}</span>}
						description="Lượt AI của thành viên ưu tiên khoá này (KHÔNG trừ hạn mức gói). Hết quota/lỗi → tự fallback khoá hệ thống." />
				) : (
					<Alert type="info" showIcon style={{marginBottom: 16}}
						message="Chưa có khoá riêng — thành viên đang dùng khoá hệ thống (tính vào hạn mức token của gói)." />
				)}

				<div className="form">
					<Controller control={control} name="provider" render={({field}) => (
						<SelectField label="Nhà cung cấp" options={AI_PROVIDERS} {...field} />
					)} />
					<Controller control={control} name="api_key"
						rules={{required: configured ? false : 'Vui lòng nhập API key'}}
						render={({field}) => (
							<InputField label="API key" type="password" errors={errors}
								placeholder={configured ? 'Nhập khoá mới để thay (để trống nếu giữ nguyên)' : 'Dán API key'}
								{...field} />
						)} />
					<Controller control={control} name="model" render={({field}) => (
						<InputField label={`Model (tuỳ chọn — mặc định: ${hint.model})`} placeholder={hint.model} {...field} />
					)} />
					{provider === 'openai' && (
						<Controller control={control} name="base_url" render={({field}) => (
							<InputField label="Base URL (tuỳ chọn)" placeholder="vd: https://api.groq.com/openai/v1" {...field} />
						)} />
					)}
					<div style={{fontSize: 13, color: '#6b7280', marginBottom: 14}}>
						<FontAwesomeIcon icon="fa-light fa-circle-info" /> Lấy khoá tại: <strong>{hint.get}</strong>
					</div>
					<Space>
						<AntdButton type="primary" loading={saving} onClick={handleSubmit(submit)}>Lưu khoá</AntdButton>
						{configured && (
							<Popconfirm title="Xoá khoá riêng của thành viên?" onConfirm={remove} okText="Xoá" cancelText="Thôi">
								<AntdButton danger loading={deleting}>Xoá khoá</AntdButton>
							</Popconfirm>
						)}
					</Space>
				</div>
			</Col>
		</Row>
	);
}

/* ===================== Tab: Trạng thái & Bảo mật ===================== */
function SecurityTab({id, m, notification}) {
	const [setStatus, {isLoading: statusLoading}] = useSetMemberStatusMutation();
	const [logoutAll, {isLoading: logoutLoading}] = useMemberLogoutAllMutation();
	const [resetPassword, {isLoading: pwLoading}] = useResetMemberPasswordMutation();
	// Trạng thái đang gửi — để chỉ hiện spinner ở đúng nút bấm, các nút còn lại bị disable.
	const [pendingStatus, setPendingStatus] = useState(null);
	const {control, handleSubmit, reset: resetForm, watch, formState: {errors}} = useForm({defaultValues: {password: '', password_confirm: ''}});

	const changeStatus = async (status) => {
		setPendingStatus(status);
		try {
			await setStatus({id, status}).unwrap();
			notification.success({message: 'Đã cập nhật trạng thái'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		} finally {
			setPendingStatus(null);
		}
	};

	const doLogoutAll = async () => {
		try {
			await logoutAll(id).unwrap();
			notification.success({message: 'Đã buộc đăng xuất mọi thiết bị'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const doResetPassword = async (values) => {
		try {
			await resetPassword({id, password: values.password}).unwrap();
			notification.success({message: 'Đã đặt lại mật khẩu (đã buộc đăng xuất)'});
			resetForm({password: '', password_confirm: ''});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	return (
		<Row gutter={[24, 24]}>
			<Col xs={24} md={12}>
				<div className={cn('section-title')}>Trạng thái tài khoản</div>
				<Space wrap>
					{STATUS_OPTIONS.map((s) => (
						<Popconfirm key={s.value} title={`Đổi trạng thái sang "${s.label}"?`}
							onConfirm={() => changeStatus(s.value)} okText="Đổi" cancelText="Thôi"
							disabled={m.status === s.value || statusLoading}>
							<AntdButton type={m.status === s.value ? 'primary' : 'default'}
								disabled={m.status === s.value || statusLoading}
								loading={pendingStatus === s.value}>
								{s.label}
							</AntdButton>
						</Popconfirm>
					))}
				</Space>

				<div className={cn('section-title')} style={{marginTop: 24}}>Phiên đăng nhập</div>
				<Popconfirm title="Buộc đăng xuất mọi thiết bị của thành viên?" onConfirm={doLogoutAll} okText="Đăng xuất" cancelText="Thôi">
					<AntdButton danger loading={logoutLoading} icon={<FontAwesomeIcon icon="fa-light fa-right-from-bracket" />}>
						Buộc đăng xuất
					</AntdButton>
				</Popconfirm>
			</Col>

			<Col xs={24} md={12}>
				<div className={cn('section-title')}>Đặt lại mật khẩu</div>
				<div className="form">
					<Controller control={control} name="password"
						rules={{required: 'Nhập mật khẩu', minLength: {value: 6, message: 'Tối thiểu 6 ký tự'}}}
						render={({field}) => (
							<InputField label="Mật khẩu mới" type="password" placeholder="Ít nhất 6 ký tự" errors={errors} {...field} />
						)} />
					<Controller control={control} name="password_confirm"
						rules={{
							required: 'Nhập lại mật khẩu mới',
							validate: (v) => v === watch('password') || 'Mật khẩu nhập lại không khớp',
						}}
						render={({field}) => (
							<InputField label="Nhập lại mật khẩu mới" type="password" placeholder="Gõ lại đúng mật khẩu trên" errors={errors} {...field} />
						)} />
					<Popconfirm title={`Đặt lại mật khẩu của ${m.fullname || 'thành viên'}?`}
						description="Thành viên sẽ bị đăng xuất khỏi mọi thiết bị."
						onConfirm={handleSubmit(doResetPassword)} okText="Đặt lại" cancelText="Thôi">
						<AntdButton type="primary" loading={pwLoading}>
							Đặt lại mật khẩu
						</AntdButton>
					</Popconfirm>
				</div>
			</Col>
		</Row>
	);
}

/* ===================== Tab: Hoạt động ===================== */
function ActivityTab({id}) {
	const {data: events = [], isLoading} = useGetMemberActivityQuery(id);

	if (isLoading) return <Loading />;

	if (!events.length) {
		return <Alert type="info" showIcon message="Chưa có hoạt động nào." />;
	}

	const dotColor = {login: 'blue', activity: 'gray', plan: 'purple', token: 'green'};

	return (
		<Timeline
			items={events.map((ev, i) => ({
				key: i,
				color: dotColor[ev.type] || 'gray',
				children: (
					<div>
						<div style={{fontWeight: 600}}>{ev.label}</div>
						<div style={{fontSize: 12, color: '#6b7280'}}>
							{ev.created ? dayjs(ev.created).format('HH:mm DD/MM/YYYY') : ''}
							{ev.meta?.ip ? ` · IP ${ev.meta.ip}` : ''}
							{ev.meta?.note ? ` · ${ev.meta.note}` : ''}
						</div>
					</div>
				),
			}))}
		/>
	);
}

export default MemberDetail;
